import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { ListBulletIcon } from '@heroicons/react/24/outline';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * SpendingByCategory Component
 * 
 * Displays spending breakdown by category in a donut chart
 * Uses Chart.js for visualization with callout labels
 * NOW INTERACTIVE: Click on any category to view filtered transactions
 * 
 * @param {Object} props Component props
 * @param {Array} props.categoryBreakdown Array of spending categories with amounts
 * @param {boolean} props.isLoading Loading state indicator
 * @param {Object} props.dateRange Optional date range to pass to transactions page
 * @param {boolean} props.isExpanded Whether the card is expanded
 * @returns {JSX.Element} SpendingByCategory component
 */
const SpendingByCategory = ({ categoryBreakdown = [], isLoading = false, dateRange = null, isExpanded = false }) => {
  console.log('SpendingByCategory - Received data:', categoryBreakdown);
  const navigate = useNavigate();
  const [hoveredSegment, setHoveredSegment] = React.useState(null);
  const [showLegend, setShowLegend] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);
  
  // Track window size for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      // On desktop, always show legend; on mobile, respect showLegend state
      if (window.innerWidth >= 1024) {
        setShowLegend(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Sync legend visibility with card expansion on mobile
  React.useEffect(() => {
    if (isExpanded) {
      setShowLegend(true);
    } else {
      // Only hide on mobile (when window width < 1024px)
      if (!isDesktop) {
        setShowLegend(false);
      }
    }
  }, [isExpanded, isDesktop]);
  
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  // Enhanced empty data checking
  if (!categoryBreakdown || 
      !Array.isArray(categoryBreakdown) ||
      categoryBreakdown.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 mb-2">No spending categories available</p>
        <p className="text-sm text-gray-400">Add transactions to see category breakdown</p>
      </div>
    );
  }

  // Normalize data to handle different property names
  const normalizedData = categoryBreakdown.map(item => ({
    category: item.category || item.name || 'Unknown',
    amount: typeof item.amount === 'number' ? item.amount : 0,
    percentage: typeof item.percentage === 'number' ? item.percentage : 0
  }));

  // Filter out any items with zero amount
  const filteredData = normalizedData.filter(item => item.amount > 0);
  
  if (filteredData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 mb-2">No categorized spending data</p>
        <p className="text-sm text-gray-400">Add categorized transactions to see breakdown</p>
      </div>
    );
  }

  // Check for zero total amount
  const totalAmount = filteredData.reduce((sum, item) => sum + (item.amount || 0), 0);
  if (totalAmount === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 mb-2">No spending amounts found</p>
        <p className="text-sm text-gray-400">Add transactions with amounts to see categories</p>
      </div>
    );
  }

  const chartColors = [
    'rgba(79, 70, 229, 0.8)',   // Indigo (Housing)
    'rgba(6, 182, 212, 0.8)',    // Cyan (Food)
    'rgba(249, 115, 22, 0.8)',   // Orange (Transportation)
    'rgba(236, 72, 153, 0.8)',   // Pink (Utilities)
    'rgba(139, 92, 246, 0.8)',   // Purple (Entertainment)
    'rgba(244, 63, 94, 0.8)',    // Rose (Healthcare)
    'rgba(16, 185, 129, 0.8)',   // Emerald (Personal)
    'rgba(107, 114, 128, 0.8)',  // Gray (Miscellaneous)
    'rgba(251, 146, 60, 0.8)',   // Amber (Shopping)
    'rgba(14, 165, 233, 0.8)',   // Sky (Education)
    'rgba(168, 85, 247, 0.8)',   // Fuchsia (Travel)
    'rgba(234, 179, 8, 0.8)',    // Yellow (Gifts)
    'rgba(34, 197, 94, 0.8)',    // Green (Savings)
    'rgba(239, 68, 68, 0.8)',    // Red (Debt)
    'rgba(20, 184, 166, 0.8)',   // Teal (Insurance)
    'rgba(45, 212, 191, 0.8)',   // Cyan-500 (Subscriptions)
  ];

  // Handle chart click to navigate to transactions filtered by category
  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const categoryName = filteredData[elementIndex].category;
      
      // Build query params with category and optional date range
      const params = new URLSearchParams();
      params.set('category', categoryName);
      
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        params.set('startDate', dateRange.startDate.toISOString().split('T')[0]);
        params.set('endDate', dateRange.endDate.toISOString().split('T')[0]);
      }
      
      // Navigate to transactions page with filters
      console.log('📊 Drilling down to category:', categoryName, 'with date range:', dateRange);
      navigate(`/transactions?${params.toString()}`);
    }
  };
  
  // Handle hover to show cursor pointer
  const handleChartHover = (event, elements) => {
    event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    if (elements.length > 0) {
      setHoveredSegment(elements[0].index);
    } else {
      setHoveredSegment(null);
    }
  };

  const chartData = {
    labels: filteredData.map(item => item.category),
    datasets: [
      {
        data: filteredData.map(item => item.amount),
        backgroundColor: chartColors.slice(0, filteredData.length),
        borderColor: 'white',
        borderWidth: 2,
        // Explicitly disable any labels on the dataset
        datalabels: {
          display: false
        }
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: isExpanded ? 20 : 8
    },
    elements: {
      arc: {
        borderWidth: 0,
        hoverBorderWidth: 4,
        hoverBorderColor: 'rgba(255, 255, 255, 0.8)'
      }
    },
    plugins: {
      legend: {
        display: isDesktop || showLegend, // Always show on desktop (>=1024px), conditional on mobile
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: isExpanded ? 15 : 8,
          font: {
            size: isExpanded ? 12 : 9,
            family: "'Inter', 'system-ui', sans-serif"
          },
          boxWidth: isExpanded ? 12 : 8,
          boxHeight: isExpanded ? 12 : 8
        },
        onHover: (event) => {
          event.native.target.style.cursor = 'pointer';
        },
        onLeave: (event) => {
          event.native.target.style.cursor = 'default';
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context) {
            const value = Math.abs(context.parsed);
            return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
          afterLabel: function() {
            return '\n💡 Click to view transactions';
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    onClick: handleChartClick,
    onHover: handleChartHover
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 relative group min-h-0">
        <Doughnut data={chartData} options={chartOptions} />
        {/* Interactive hint - only show when expanded */}
        {isExpanded && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="bg-violet-600 text-white text-xs px-3 py-1 rounded-full shadow-lg whitespace-nowrap">
              💡 Click any segment to view transactions
            </div>
          </div>
        )}
      </div>
      {/* Legend Toggle Button - Only visible on mobile, centered below chart */}
      <div className="lg:hidden flex justify-center mt-0.5">
        <button
          onClick={() => setShowLegend(!showLegend)}
          className={`flex items-center gap-0.5 text-[7px] leading-none px-0.5 py-0 rounded transition-colors ${
            showLegend 
              ? 'bg-violet-100 text-violet-700 hover:bg-violet-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          style={{ height: 'fit-content', minHeight: 'auto' }}
          aria-label={showLegend ? 'Hide legend' : 'Show legend'}
        >
          <ListBulletIcon className="h-2 w-2" />
          <span>{showLegend ? 'Hide' : 'Show'} Legend</span>
        </button>
      </div>
    </div>
  );
};

export default SpendingByCategory; 